import { useTranslation } from 'react-i18next';
import { SafeAreaView, ScrollView, SxProp, View, Pressable, useDripsyTheme, Image } from 'dripsy';
import React from 'react';

import { useStore } from 'store/store';
import { UniButton } from '@/design-system/components/buttons';
import { UniCheckbox } from '@/design-system/components/inputs';
import { Icon } from '@/design-system/components/icons/Icon';
import { TextTitle, H5 } from '@/design-system/components/typography';

interface DiscoverLandingScreenProps {
  loading: boolean;
  openAccount: () => void;
}

export const DiscoverLanding: React.FC<DiscoverLandingScreenProps> = (props) => {
  const { t } = useTranslation('earn');
  const store = useStore();
  const { loading, openAccount } = props;
  const { theme } = useDripsyTheme();
  const [isAcknowledged, setIsAcknowledged] = React.useState(false);

  return (
    <SafeAreaView sx={sxStyles.container}>
      <View sx={sxStyles.container}>
        {/* Content */}
        <ScrollView sx={sxStyles.scrollContent}>
          <View sx={sxStyles.scrollContentContainer}>
            <Image
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                borderTopLeftRadius: 25,
                borderTopRightRadius: 25
              }}
              source={require('assets/images/discover.png')}
            />
            {/* Upper */}
            <View sx={sxStyles.description}>
              {/* Image */}
              <View sx={sxStyles.descriptionImage}>
                <Icon name="landingPageNative" height={135} width={375} />
              </View>
              {/* Description */}
              <View sx={sxStyles.descriptionTexts}>
                <H5 bold sx={{ textAlign: 'center', color: '$textTheme' }}>
                  {t('earn-invest::glossary.earn-and-invest', { vendor: store.vendor })}
                </H5>
                <View sx={{ height: 8 }} />
                <TextTitle semiBold sx={{ textAlign: 'center', color: '$textDarkBackground' }}>
                  {t('earn-invest::glossary.subscription-to-offers')}
                </TextTitle>
                <View sx={{ height: 10 }} />
                <View sx={sxStyles.descriptionButtonContainer}>
                  <Pressable onPress={() => {}}>
                    <View sx={sxStyles.descriptionButton}>
                      <Icon name="arrowRight" height={20} width={20} color={theme.colors?.$hyperlink as string} />
                    </View>
                  </Pressable>
                </View>
              </View>
            </View>
            {/* Acknowledgement */}
            <View sx={sxStyles.acknowledgement}>
              <UniCheckbox
                label={t('earn-invest::earn-landing.text--application-confirmation', { vendor: store.brand })}
                value={isAcknowledged}
                onValueChange={() => {
                  setIsAcknowledged(!isAcknowledged);
                }}
                options={{ labelColor: '$textDarkBackground' }}
              />
              <View sx={sxStyles.openAccountButton}>
                <UniButton
                  label={t('earn-invest::glossary.open-account')}
                  onPress={openAccount}
                  loading={loading}
                  disabled={loading || !isAcknowledged}
                />
              </View>
            </View>
            {/* Button */}
          </View>
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    flex: 1,
    flexDirection: 'column',
    backgroundColor: '$backgroundSecondary'
  },
  scrollContent: {
    flex: 1,
    height: '100%',
    backgroundColor: '$screenBackgroundPrimary'
  },
  scrollContentContainer: {
    flex: 1,
    height: '100%',
    paddingY: 20,
    backgroundColor: '$backgroundPrimary'
  },
  sectionDivider: {
    height: 5,
    backgroundColor: '$backgroundSecondary'
  },
  description: {
    paddingX: 16,
    paddingY: 28,
    alignItems: 'center'
  },
  descriptionTexts: {
    mt: 13
  },
  descriptionTitle: {
    mb: 4,
    fontWeight: 'bolder',
    fontSize: '$4',
    lineHeight: '$4',
    textAlign: 'center'
  },
  descriptionSummary: {
    mb: 9,
    fontWeight: 'bold',
    fontSize: '$2',
    lineHeight: '$2',
    textAlign: 'center'
  },
  descriptionButtonContainer: {
    flexDirection: 'row',
    justifyContent: 'center'
  },
  descriptionButton: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center'
  },
  descriptionButtonText: {
    fontSize: '$0',
    lineHeight: '$0',
    color: '$textAssist'
  },
  acknowledgement: {
    flex: 1,
    paddingX: 16,
    paddingY: 20
  },
  openAccountButton: {
    paddingY: 20
  }
};
